import { useState, useEffect, useRef } from 'react';
import '../../styles/ServiceRichText.css';
import cloud from '../../assets/serviceDetails/cloud.png'


interface Content {
 percent: number;
 barColor:string;
 topic:string;
}

interface ServiceRichTextProps {
  title: string;
  subTitle? : string;
  content:Content[];
}

const ServiceRichText: React.FC<ServiceRichTextProps> = ({ 
  title, 
  subTitle,
  content, 
}) => {

  const [fillWidths, setFillWidths] = useState<number[]>(content.map(() => 0));
  const componentRef = useRef<HTMLDivElement>(null);
  const animationRefs = useRef<number[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Start animations when visible
            content.forEach((item, index) => {
              const startTime = performance.now();
              const duration = 1000;
              
              const animate = (currentTime: number) => {
                const elapsedTime = currentTime - startTime;
                const progress = Math.min(elapsedTime / duration, 1);
                setFillWidths(prev => {
                  const newWidths = [...prev];
                  newWidths[index] = progress * item.percent;
                  return newWidths;
                });
                
                if (progress < 1) {
                  const id = requestAnimationFrame(animate);
                  animationRefs.current.push(id);
                }
              };
              
              const id = requestAnimationFrame(animate);
              animationRefs.current.push(id);
            });
          } else {
            // Immediately reset to 0 when not visible
            setFillWidths(content.map(() => 0));
            // Cancel all running animations
            animationRefs.current.forEach(id => cancelAnimationFrame(id));
            animationRefs.current = [];
          }
        });
      },
      { threshold: 0.1 }
    );

    const currentRef = componentRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
      animationRefs.current.forEach(id => cancelAnimationFrame(id));
    };
  }, [content]);
  
  const renderRichText = () => {
    return (
      <div className="rl-rich-text-container" ref={componentRef} >
        {/* Heading */}
        <div className='rich-text-heading'>
          <p className='sections-title'>{title}</p>
          <p className='description'>{subTitle}</p>
        </div>

        {/* stats container*/}
        <div className='rl-stats-content'>
          <div className='stats-img-container'>
            <img src={cloud} alt="img"></img>
          </div>
          <div className="rl-stats-container">
            {content.map((data, index) => (
              <div className="rl-stat-card" key={index}>
                <div 
                  className="rl-stat-value"
                  style={{ color: data.barColor }}
                >
                  {data.percent}<span className="rl-stat-percent">%</span>
                </div>
                
                <div className="rl-progress-bar">
                  <div 
                    className="rl-progress-fill" 
                    style={{ 
                      width: `${fillWidths[index]}%`,
                      background: data.barColor 
                    }}
                  ></div>
                </div>
        
                <div className="rl-stat-label">{data.topic}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rl-rich-text-inner">

          {/* Challenge section */}
          <div className="rl-content-section">
            <h2 className="rl-section-title">
              The Cloud Cost Challenge
            </h2>
            <p className="rl-section-text">
              If you're spending more on cloud infrastructure than you'd planned — and still don't have clear answers about 
              what's driving that spend — you're not alone. Most fast-growing companies reach a point where cloud bills 
              become unpredictable, cost accountability is unclear, and engineering teams are forced to choose between 
              speed and efficiency.
            </p>
            <div className="rl-learn-more">
              <span>Learn more about our approach</span>
            </div>
          </div>

          {/* Solution section */}
          <div className="rl-content-section">
            <h2 className="rl-section-title">
              Our Solution
            </h2>
            <p className="rl-section-text">
              Our FinOps Strategy Services are designed to help you turn cloud spending from a liability into a lever for 
              scale. We help you build a cloud cost operating model that gives your engineering and finance teams shared 
              visibility, shared responsibility, and the power to act.
            </p>
            <p className="rl-section-text">
              We start by understanding your cloud usage patterns, business goals, and technical architecture. From there, 
              we design a cost optimization framework tailored to your environment — whether you're running on AWS, GCP, 
              Azure, or Kubernetes.
            </p>
            <div className="rl-learn-more">
              <span>Learn more about our approach</span>
            </div>
          </div>      
        </div>
      </div>
    );
  };

  return (
    <section className='service-rich-text'>
          {renderRichText()}
    </section>
  );
};

export default ServiceRichText; 